import { cn } from 'ui/lib/utils';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

interface DialogProps {
	open: boolean;
	onClose: () => void;
	title: string;
	children: ReactNode;
	destructive?: boolean;
	confirmText?: string;
	size?: 'sm' | 'md' | 'lg';
	className?: string;
	'data-testid'?: string;
}

const sizeMap = {
	sm: '400px',
	md: '560px',
	lg: '720px'
} as const;

export function Dialog({
	open,
	onClose,
	title,
	children,
	destructive = false,
	confirmText,
	size = 'md',
	className,
	'data-testid': testId = 'dialog'
}: DialogProps) {
	const dialogRef = useRef<HTMLDialogElement>(null);
	const [confirmInput, setConfirmInput] = useState('');

	// Sync open prop with native dialog
	useEffect(() => {
		const dialog = dialogRef.current;
		if (!dialog) return;

		if (open && !dialog.open) {
			dialog.showModal();
		} else if (!open && dialog.open) {
			dialog.close();
		}
	}, [open]);

	// Reset confirmation input when dialog opens/closes
	useEffect(() => {
		if (!open) {
			setConfirmInput('');
		}
	}, [open]);

	// Handle native close event (e.g. Escape key)
	const handleClose = useCallback(() => {
		onClose();
	}, [onClose]);

	// Handle backdrop click
	const handleBackdropClick = useCallback(
		(e: React.MouseEvent<HTMLDialogElement>) => {
			if (e.target === dialogRef.current) {
				onClose();
			}
		},
		[onClose]
	);

	const isConfirmDisabled = destructive && confirmText ? confirmInput !== confirmText : false;

	return (
		<dialog
			ref={dialogRef}
			data-testid={testId}
			onClose={handleClose}
			onClick={handleBackdropClick}
			className={cn('m-0 bg-transparent p-0', className)}
			style={
				{
					'--dialog-width': sizeMap[size]
				} as React.CSSProperties
			}>
			<div
				className="flex max-h-[90vh] min-h-0 w-[var(--dialog-width)] max-w-[90vw] flex-col overflow-hidden"
				style={{
					backgroundColor: 'var(--safi-bg-card)',
					borderRadius: 'var(--safi-radius-md)',
					boxShadow: 'var(--shadow-lg)',
					margin: 'auto'
				}}
				onClick={(e) => e.stopPropagation()}>
				{/* Header */}
				<div
					className="flex items-center justify-between px-6 py-4"
					style={{
						borderBottom: '1px solid var(--safi-border-light)'
					}}>
					<h2
						className="text-headline"
						style={{
							color: destructive ? 'var(--color-destructive)' : 'var(--safi-text-primary)'
						}}>
						{title}
					</h2>
					<button
						type="button"
						onClick={onClose}
						aria-label="Cerrar diálogo"
						className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-full transition-colors hover:bg-[var(--safi-bg-table-header)]"
						style={{ color: 'var(--safi-text-secondary)' }}>
						<svg
							width="16"
							height="16"
							viewBox="0 0 16 16"
							fill="none"
							xmlns="http://www.w3.org/2000/svg">
							<path
								d="M12 4L4 12M4 4l8 8"
								stroke="currentColor"
								strokeWidth="1.5"
								strokeLinecap="round"
							/>
						</svg>
					</button>
				</div>

				{/* Body */}
				<div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>

				{/* Destructive confirmation */}
				{destructive && confirmText && (
					<div
						className="px-6 pb-4"
						style={{ borderTop: '1px solid var(--safi-border-light)' }}>
						<p
							className="text-footnote mb-2 pt-4"
							style={{ color: 'var(--safi-text-secondary)' }}>
							Para confirmar, escriba <strong style={{ color: 'var(--color-destructive)' }}>{confirmText}</strong> a
							continuación:
						</p>
						<input
							type="text"
							value={confirmInput}
							onChange={(e) => setConfirmInput(e.target.value)}
							placeholder={confirmText}
							data-testid="dialog-confirm-input"
							className="w-full rounded-[var(--safi-radius-sm)] border px-3 py-2 text-sm"
							style={{
								borderColor: 'var(--safi-border-light)',
								backgroundColor: 'var(--safi-bg-layout)',
								color: 'var(--safi-text-primary)'
							}}
							autoComplete="off"
						/>
						{isConfirmDisabled && confirmInput.length > 0 && (
							<p
								className="text-caption-1 mt-1"
								style={{ color: 'var(--color-destructive)' }}>
								El texto no coincide
							</p>
						)}
					</div>
				)}
			</div>

			{/* Styles for native dialog backdrop */}
			<style>{`
        dialog[data-testid="${testId}"]::backdrop {
          background-color: rgba(26, 55, 98, 0.4);
        }
        dialog[data-testid="${testId}"]:not([open]) {
          display: none;
        }
        dialog[data-testid="${testId}"] {
          position: fixed;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          border: none;
          max-width: 100vw;
          max-height: 100vh;
          width: 100vw;
          height: 100vh;
        }
      `}</style>
		</dialog>
	);
}

export { type DialogProps };

export default Dialog;
